/* Billing — STORY-032.
 *
 * **This screen is where Stripe returns the customer**, and that is the reason
 * it exists rather than a nice-to-have. The checkout's success and cancel urls
 * point here, and until it was built they pointed at a route that did not
 * exist: somebody who had just paid landed on "Not found".
 *
 * Activation was never affected — it is driven by the webhook and never by the
 * redirect, which is exactly what STORY-032 requires. What was broken was the
 * only part the customer could see, and "Not found" straight after paying reads
 * as a failed payment.
 *
 * ── the redirect is still not evidence ───────────────────────────────────────
 * The banner below says a payment was *received*, never that the plan is
 * active. Anyone can visit this url with `?checkout=done` appended. What the
 * screen shows as fact is the subscription the API returns; the banner is only
 * an acknowledgement that the customer came back from somewhere.
 */

import { formatMoney, grantsEntitlement, money, parseCurrency } from '@eventuley/domain';
import { formatDate, type MessageKey } from '@eventuley/i18n';
import { Link, useParams, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import { useI18n, useTranslate } from '../../../shared/i18n/index.js';
import { Button, Callout } from '../../../shared/ui/index.js';
import { useSetCancellation, useSubscription, useSubscriptionHistory } from '../api/index.js';
import { BillingHistory } from '../components/billing-history.js';

const PLAN_NAME: Record<string, MessageKey> = {
  free: 'plans.free',
  pro: 'plans.pro',
  business: 'plans.business',
  enterprise: 'plans.enterprise',
};

const STATUS_LABEL: Record<string, MessageKey> = {
  incomplete: 'billing.status_incomplete',
  active: 'billing.status_active',
  past_due: 'billing.status_past_due',
  unpaid: 'billing.status_unpaid',
  canceled: 'billing.status_canceled',
};

export function BillingScreen() {
  const t = useTranslate();
  const { locale, formatLocale } = useI18n();
  /* The VIEWER's zone. `i18n.md` reserves the venue-timezone rule for an
   * Event's own time; a renewal date is ours and belongs in the reader's. */
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { organisationId } = useParams({ strict: false }) as { organisationId?: string };
  const { checkout } = useSearch({ strict: false }) as { checkout?: string };

  const { data, isPending, isError } = useSubscription(organisationId);
  /* Owner-only on the server; a Member gets 403 and the section says the
   * history could not be loaded, which is honest — they may not see it. */
  const history = useSubscriptionHistory(organisationId);

  /* A subscription ROW is not a subscription the workspace still has.
   *
   * A cancelled one keeps its plan code — that is the record of what was
   * bought — and this screen read that field directly. So a workspace already
   * back on Free was told it was on Pro, offered a Cancel button for a
   * subscription that had already ended, and given no way to buy again.
   * Reported 2026-09-05.
   *
   * `grantsEntitlement` is the same rule the API uses to decide what the
   * workspace may actually do, imported rather than restated: a second copy of
   * "which statuses still count" drifts, and this screen disagreeing with the
   * server about whether somebody is paying is the worst place for it to.
   * `past_due` deliberately still counts — that customer is inside Stripe's
   * retry window and has lost nothing. */
  const subscription = data?.subscription ?? null;
  const entitled = subscription !== null && grantsEntitlement(subscription.status);

  return (
    <>
      <div className="page-head">
        <h1>{t('billing.title')}</h1>
      </div>

      {/* An acknowledgement, not a claim. See the header. */}
      {checkout === 'done' ? <Callout tone="success">{t('billing.checkout_done')}</Callout> : null}
      {checkout === 'cancelled' ? (
        <Callout tone="info">{t('billing.checkout_cancelled')}</Callout>
      ) : null}

      {isPending ? <p className="field__hint">{t('billing.loading')}</p> : null}

      {/* An error and an empty subscription must not look the same. A workspace
          told it has no subscription when the request merely failed would
          conclude it was never charged. */}
      {isError ? <Callout tone="warning">{t('billing.unavailable')}</Callout> : null}

      {!entitled && !isPending && !isError ? (
        <>
          {/* Two different sentences. Never having subscribed and having had a
              subscription end are not the same fact, and telling somebody who
              paid last month that they have "no subscription" reads as though
              we mislaid it. */}
          <p className="field__hint">
            {t(subscription === null ? 'billing.none' : 'billing.ended')}
          </p>
          <p>
            <Link
              className="btn btn--primary"
              to="/plans"
              search={{ workspace: organisationId ?? '' }}
            >
              {t('billing.see_plans')}
            </Link>
          </p>
        </>
      ) : null}

      {!entitled || subscription === null ? null : (
        <>
          <dl className="billing__summary">
            <div className="billing__row">
              <dt>{t('billing.plan')}</dt>
              <dd>{t(PLAN_NAME[subscription.planCode] ?? 'plans.free')}</dd>
            </div>
            <div className="billing__row">
              <dt>{t('billing.status')}</dt>
              <dd>{t(STATUS_LABEL[subscription.status] ?? 'billing.status_incomplete')}</dd>
            </div>
            <div className="billing__row">
              <dt>{t('billing.amount')}</dt>
              <dd>
                {(() => {
                  /* Integer minor units through the formatter, with the digit
                     count from the currency — JPY has none, KWD has three.
                     ADR-0005, `i18n.md`. */
                  const code = parseCurrency(subscription.currency);
                  return code
                    ? formatMoney(money(subscription.amountMinor, code), locale)
                    : String(subscription.amountMinor);
                })()}
                {/* The figure above is the PLAN's price, and it is not what the
                    card was charged: Stripe is the merchant of record and adds
                    the tax due where the customer is, which we neither compute
                    nor hold. A live payment of $19.00 took $20.54.

                    Saying nothing left somebody comparing this screen with
                    their bank statement to conclude they had been overcharged,
                    which is the worst thing a billing page can imply. */}
                <span className="field__hint billing__amount-note">
                  {t('billing.amount_excludes_tax')}
                </span>
              </dd>
            </div>
            {subscription.currentPeriodEnd === null ? null : (
              <div className="billing__row">
                <dt>{t('billing.renews')}</dt>
                {/* Says which it is. "Renews on the 3rd" and "ends on the 3rd"
                    are the same date and opposite facts, and a customer who has
                    just cancelled needs to see that it took. */}
                <dd>
                  {t(subscription.cancelAtPeriodEnd ? 'billing.ends_on' : 'billing.renews_on', {
                    when: formatDate(new Date(subscription.currentPeriodEnd), {
                      locale: formatLocale,
                      timeZone,
                    }),
                  })}
                </dd>
              </div>
            )}
          </dl>

          <CancelControl
            organisationId={organisationId}
            cancelAtPeriodEnd={subscription.cancelAtPeriodEnd}
          />

          <p className="field__hint">{t('billing.managed_by_stripe')}</p>
          <p>
            <Link className="btn" to="/plans" search={{ workspace: organisationId ?? '' }}>
              {t('billing.see_plans')}
            </Link>
          </p>
        </>
      )}
      {/* Always shown, whatever the subscription's state. A workspace back on
          Free is exactly the one whose owner wants to know why. */}
      <BillingHistory
        changes={history.data?.changes}
        isPending={history.isPending}
        isError={history.isError}
      />
    </>
  );
}

/**
 * Cancelling, and undoing it — STORY-034.
 *
 * **As reachable as subscribing**, which is the story's own requirement and a
 * legal one in a growing number of jurisdictions: no hidden link, no maze, no
 * "contact support".
 *
 * Confirmed once, because it is not reversible from the customer's point of
 * view within the moment — but the confirmation says plainly that nothing is
 * deleted and it can be undone, so it informs rather than obstructs. A
 * confirmation designed to make somebody give up is the dark pattern this
 * screen exists to avoid.
 */
function CancelControl({
  organisationId,
  cancelAtPeriodEnd,
}: {
  readonly organisationId: string | undefined;
  readonly cancelAtPeriodEnd: boolean;
}) {
  const t = useTranslate();
  const [confirming, setConfirming] = useState(false);
  const setCancellation = useSetCancellation(organisationId);

  /* Resuming needs no confirmation: it is the safe direction, and asking "are
     you sure you want to keep paying us" is the sort of friction that belongs
     in the cancel path and nowhere near this one. */
  if (cancelAtPeriodEnd) {
    return (
      <div className="btn-row">
        <Button
          variant="secondary"
          busy={setCancellation.isPending}
          onClick={() => setCancellation.mutate(false)}
        >
          {t('billing.resume')}
        </Button>
        {setCancellation.isError ? (
          <Callout tone="danger">{t('billing.cancel_failed')}</Callout>
        ) : null}
      </div>
    );
  }

  if (!confirming) {
    return (
      <div className="btn-row">
        <Button variant="secondary" onClick={() => setConfirming(true)}>
          {t('billing.cancel')}
        </Button>
        {setCancellation.isSuccess ? (
          <Callout tone="info">{t('billing.cancel_requested')}</Callout>
        ) : null}
        {setCancellation.isError ? (
          <Callout tone="danger">{t('billing.cancel_failed')}</Callout>
        ) : null}
      </div>
    );
  }

  return (
    <Callout tone="warning">
      <strong>{t('billing.cancel_confirm')}</strong>
      <p>{t('billing.cancel_confirm_body')}</p>
      <div className="btn-row">
        <Button
          busy={setCancellation.isPending}
          onClick={() => {
            setCancellation.mutate(true, { onSettled: () => setConfirming(false) });
          }}
        >
          {t('billing.cancel_confirm_yes')}
        </Button>
        {/* Escapable, and the escape is the default-looking one. */}
        <Button variant="secondary" onClick={() => setConfirming(false)}>
          {t('billing.cancel_confirm_no')}
        </Button>
      </div>
    </Callout>
  );
}
